/**
 * 관리자 페이지 공통 UI 토큰
 *
 * 목적: chip, 배지, 액션 버튼 등 반복되는 디자인을 한 곳에서 관리
 * 사용: import { actionBtnSx, statusChipSx, getStatusColors } from '../styles/uiTokens'
 */

// ── 액션 버튼 (수정/삭제/해지/환불 등) ────────────────
// 모든 페이지 테이블의 액션 버튼은 이 sx 사용 → 동일 라운드/패딩
export const actionBtnSx = {
  // 상태 chip과 달리 행동 버튼은 직사각형 control로 고정한다.
  borderRadius: '8px',
  fontSize: 12,
  fontWeight: 700,
  minWidth: 56,
  minHeight: 32,
  px: 1.25,
  py: 0,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  whiteSpace: 'nowrap',      // "해지취소" 같은 텍스트 줄바꿈 방지 ⭐
  boxShadow: 'none',
  textTransform: 'none',
  letterSpacing: '-0.01em',
  flexShrink: 0,             // flex 컨테이너에서 압축되지 않음
  overflow: 'hidden',        // hover/ripple 음영을 버튼의 8px 모서리 안에서만 보이게 한다.
  transition: 'background-color 120ms ease, filter 120ms ease',
  '&:hover': { boxShadow: 'none', filter: 'brightness(0.95)' },
};

// 더 작은 액션 버튼 (인라인 등 좁은 공간)
export const actionBtnSmSx = {
  ...actionBtnSx,
  fontSize: 11,
  minWidth: 56,
  px: 1.25,
};

// 표 안에서 같은 의미를 비교하는 tag (결제수단·출처·카테고리 등).
// 색상은 각 화면의 Chip color prop을 유지하고 높이·폭·문자 정렬만 통일한다.
export const tableTagSx = (minWidth = 64) => ({
  minWidth,
  height: 24,
  boxSizing: 'border-box',
  fontSize: 11.5,
  fontWeight: 700,
  lineHeight: 1,
  justifyContent: 'center',
  '& .MuiChip-label': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    px: 1,
    lineHeight: 1,
  },
});

// 기간/필터 선택처럼 한 묶음으로 비교하는 segmented control.
export const segmentedControlSx = {
  flexShrink: 0,
  borderRadius: '8px',
  overflow: 'hidden',
  '& .MuiToggleButton-root': {
    borderRadius: 0,
    minHeight: 36,
    px: 1.5,
    py: 0,
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.2,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  '& .MuiToggleButton-root:first-of-type': {
    borderTopLeftRadius: '8px',
    borderBottomLeftRadius: '8px',
  },
  '& .MuiToggleButton-root:last-of-type': {
    borderTopRightRadius: '8px',
    borderBottomRightRadius: '8px',
  },
};

// ── 상태 chip 색상 토큰 ──────────────────────────────
// MUI 기본 color prop 대신 직접 색상 지정 — 페이지 간 통일
export const STATUS_COLORS = {
  // 긍정/활성
  success: { bg: '#DCFCE7', fg: '#15803D', border: '#BBF7D0' },
  // 진행 중/대기
  info:    { bg: '#DBEAFE', fg: '#1E40AF', border: '#BFDBFE' },
  // 경고/예약/주의
  warning: { bg: '#FEF3C7', fg: '#B45309', border: '#FDE68A' },
  // 실패/위험/취소
  danger:  { bg: '#FEE2E2', fg: '#B91C1C', border: '#FECACA' },
  // 부정/만료/환불
  neutral: { bg: '#F1F5F9', fg: '#475569', border: '#CBD5E1' },
  // 보조
  purple:  { bg: '#EDE9FE', fg: '#6D28D9', border: '#DDD6FE' },
};

// 상태 chip sx 생성기
// 사용: <Box sx={statusChipSx('success')}>활성</Box>
export const statusChipSx = (variant = 'neutral') => {
  const c = STATUS_COLORS[variant] || STATUS_COLORS.neutral;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 24,
    boxSizing: 'border-box',
    alignSelf: 'center',
    px: 1,
    py: 0,
    borderRadius: '999px',
    fontSize: 11,
    fontWeight: 700,
    backgroundColor: c.bg,
    color: c.fg,
    border: `1px solid ${c.border}`,
    letterSpacing: '-0.01em',
    minWidth: 56,
    textAlign: 'center',
    lineHeight: 1,
    whiteSpace: 'nowrap',
  };
};

// ── 알림 배지 (예: 7일 내 만료 N건) ───────────────────
// 사용: <Box sx={alertBadgeSx('warning')}><Icon/> N건</Box>
export const alertBadgeSx = (variant = 'warning') => {
  const c = STATUS_COLORS[variant] || STATUS_COLORS.warning;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0.75,
    px: 1.5,
    py: 0.6,
    borderRadius: '999px',
    backgroundColor: c.bg,
    border: `1px solid ${c.border}`,
    fontSize: 12,
    fontWeight: 700,
    color: c.fg,
    lineHeight: 1.2,
  };
};

// ── 가입 경로 chip 색상 (서비스 영역 토큰) ──────────────
export const PROVIDER_COLORS = {
  local:  { bg: '#F1F5F9', fg: '#475569', border: '#CBD5E1' },  // 이메일 — 회색
  custom: { bg: '#F1F5F9', fg: '#475569', border: '#CBD5E1' },
  phone:  { bg: '#F1F5F9', fg: '#475569', border: '#CBD5E1' },
  kakao:  { bg: '#FEF9C3', fg: '#854D0E', border: '#FDE68A' },  // 카카오 노랑
  google: { bg: '#DBEAFE', fg: '#1E40AF', border: '#BFDBFE' },  // 구글 파랑
  apple:  { bg: '#1F2937', fg: '#FFFFFF', border: '#374151' },  // 애플 검정
  naver:  { bg: '#DCFCE7', fg: '#166534', border: '#BBF7D0' },  // 네이버 초록
};

export const PROVIDER_LABELS = {
  local: '이메일',
  custom: '이메일',
  phone: '전화번호',
  kakao: '카카오',
  apple: '애플',
  google: '구글',
  naver: '네이버',
};

// 가입 경로 chip sx 생성기. 상태 배지와 같은 기준 크기를 쓰고,
// 더 긴 레이블만 내용만큼 넓어진다. 표 안에서 행마다 들쭉날쭉해 보이지 않게 한다.
export const providerChipSx = (provider) => {
  const c = PROVIDER_COLORS[provider] || PROVIDER_COLORS.local;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 24,
    boxSizing: 'border-box',
    px: 1,
    py: 0,
    borderRadius: '999px',
    fontSize: 11,
    fontWeight: 700,
    backgroundColor: c.bg,
    color: c.fg,
    border: `1px solid ${c.border}`,
    letterSpacing: '-0.01em',
    minWidth: 56,
    textAlign: 'center',
    lineHeight: 1,
    whiteSpace: 'nowrap',
  };
};
